// Shared TypeScript mirror of the backend AI-trace schema
// (building_manager_api/adaptors/ai_trace.py). One definition consumed by both apps
// and by the AITraceViewer in theme-components, so every AI surface renders identically.
// See building-manager-api docs/design-ai-trace-capture.md.

export type AITraceEntryType =
  | "system_prompt"
  | "user_prompt"
  | "thinking"
  | "text"
  | "tool_use"
  | "tool_result"
  | "tool_catalogue"
  // Per-turn marker from the AI chokepoint: the context that turn read and what
  // it cost. Mirrors bedrock_client's turn entry.
  | "turn";

// A named, independently-collapsible region within one entry's content. Recursive, so
// the viewer can collapse the noisy interior of a large entry (a file read, the system
// prompt's worked-examples block) without scrolling past everything else.
export interface AITraceSection {
  label: string;
  content?: string | null;
  sections?: AITraceSection[];
  lineCount?: number;
  truncated?: boolean;
  defaultCollapsed?: boolean;
}

export interface AITraceEntry {
  role?: string; // system | user | assistant | tool
  type?: AITraceEntryType | string;
  label?: string | null;
  content?: string | null; // flat text (when no nested sections)
  sections?: AITraceSection[] | null; // structured, nested-collapsible content
  name?: string | null; // tool name (tool_use / tool_result)
  input?: Record<string, unknown> | null; // tool input (tool_use)
  atSeconds?: number;
  modelSeconds?: number | null;
  toolSeconds?: number | null;
  truncated?: boolean;
  isError?: boolean;
}

export interface AITrace {
  entries: AITraceEntry[];
  model_id?: string;
  stop_reason?: string;
  usage?: Record<string, number>;
  totalSeconds?: number;
  truncatedForDisplay?: boolean;
}

// True when a trace has something to render.
export const hasAITrace = (trace?: AITrace | null): trace is AITrace =>
  !!trace && Array.isArray(trace.entries) && trace.entries.length > 0;
